import * as DataDogLogs from '~/lib/datadog/datadog';

/**
 * Global registry for section rerender callbacks
 * BaseSection instances can register themselves here to be notified of rerenders
 */
const sectionRerenderRegistry = new Map();

// Track latest rerender request to avoid out-of-order DOM updates
let latestRerenderRequestId = 0;
let activeFetchController = null;

/**
 * Registers a callback to be called when a section is rerendered
 * @param {string} sectionId - The section ID
 * @param {Function} callback - The callback function to call
 */
export function registerSectionRerenderCallback(sectionId, callback) {
  sectionRerenderRegistry.set(sectionId, callback);
}

/**
 * Rerenders sections using Shopify's Section Rendering API
 * @param {HTMLElement|Array<string>} target - Container element or array of section IDs
 * @param {string|Object} options - Section type or section callbacks object
 * @param {Function} onComplete - Callback function called after successful rerender
 * @param {Function} onError - Callback function called on error
 * @returns {Promise<void>}
 */
export async function rerenderSections(target, options, onComplete, onError) {
  let fetchController = null;
  try {
    // Assign a new request id and cancel any in-flight fetch
    const requestId = ++latestRerenderRequestId;
    if (activeFetchController) {
      try {
        activeFetchController.abort();
      } catch {
        // no-op
      }
    }
    fetchController = new AbortController();
    activeFetchController = fetchController;

    const isMultiple = Array.isArray(target);
    const sectionIds = isMultiple ? target : [target.getAttribute('data-section-id')];

    if (!sectionIds.every((id) => id)) {
      throw new Error('Invalid section ID(s) provided');
    }

    // Fetch sections from Shopify API with current query parameters
    const sectionsParam = sectionIds.join(',');
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('sections', sectionsParam);
    const response = await fetch(currentUrl.toString(), { signal: fetchController.signal });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // If a newer request started while awaiting, bail
    if (requestId !== latestRerenderRequestId) {
      if (activeFetchController === fetchController) {
        activeFetchController = null;
      }
      return;
    }

    const data = await response.json();

    // Process each section
    const rerenderPromises = sectionIds.map(async (sectionId) => {
      if (requestId !== latestRerenderRequestId) return;
      const sectionHtml = data[sectionId];
      if (!sectionHtml) {
        throw new Error(`Section HTML not found in response for section: ${sectionId}`);
      }

      // Find the section container
      const container = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (!container) {
        throw new Error(`Section container not found for section: ${sectionId}`);
      }

      // Update section content
      await updateSectionContent(container, sectionHtml, sectionId);

      // Call section-specific completion callback if provided
      if (requestId !== latestRerenderRequestId) return;
      if (isMultiple && options[sectionId]?.onComplete) {
        options[sectionId].onComplete(container);
      }
    });

    // Wait for all sections to be processed
    await Promise.all(rerenderPromises);

    if (requestId !== latestRerenderRequestId) {
      if (activeFetchController === fetchController) {
        activeFetchController = null;
      }
      return;
    }

    // Call the global completion callback if provided
    if (onComplete) {
      onComplete();
    }

    if (activeFetchController === fetchController) {
      activeFetchController = null;
    }
  } catch (error) {
    // Ignore deliberate cancellations
    if (error && (error.name === 'AbortError' || error.code === 20)) {
      if (activeFetchController === fetchController) {
        activeFetchController = null;
      }
      return;
    }
    DataDogLogs.error(
      '[Allbirds utils] - Section Rerenderer - Error rerendering sections',
      {},
      error,
    );

    if (onError) {
      onError(error);
    }
    // Cleanup controller reference on error if still pointing at this request
    if (activeFetchController === fetchController) {
      activeFetchController = null;
    }
  }
}

/**
 * Section Rerenderer Utility
 *
 * Provides functionality to rerender Shopify sections using the Section Rendering API.
 * Handles fetching, parsing, and updating section content with proper cleanup.
 */

/**
 * Rerenders sections by their section types, automatically resolving to section IDs
 * @param {Array<string>} sectionTypes - Array of section types to rerender
 * @param {Object} options - Section callbacks object (optional)
 * @param {Function} onComplete - Callback function called after successful rerender
 * @param {Function} onError - Callback function called on error
 * @returns {Promise<void>}
 */
export async function rerenderSectionsByType(sectionTypes, options = {}, onComplete, onError) {
  try {
    const mergedOptions = {
      preserveScroll: true,
      ...options,
    };

    // Preserve current scroll position if requested
    const currentScrollY = mergedOptions.preserveScroll ? window.scrollY : null;

    // Find all section IDs for the given section types
    const sectionIds = [];
    const sectionCallbacks = {};

    sectionTypes.forEach((sectionType) => {
      const sections = document.querySelectorAll(`[data-section-type="${sectionType}"]`);

      sections.forEach((section) => {
        const sectionId = section.getAttribute('data-section-id');

        if (sectionId) {
          sectionIds.push(sectionId);
          // If options are provided for this section type, use them
          if (mergedOptions[sectionType]) {
            sectionCallbacks[sectionId] = mergedOptions[sectionType];
          }
        }
      });
    });

    if (sectionIds.length === 0) {
      throw new Error(`No sections found for types: ${sectionTypes.join(', ')}`);
    }

    // Create wrapped callbacks that restore scroll position if requested
    const wrappedOnComplete = () => {
      // Restore scroll position if preservation was requested
      if (mergedOptions.preserveScroll && currentScrollY !== null) {
        window.scrollTo(0, currentScrollY);
      }
      if (onComplete) {
        onComplete();
      }
    };

    const wrappedOnError = (error) => {
      // Restore scroll position even on error if preservation was requested
      if (mergedOptions.preserveScroll && currentScrollY !== null) {
        window.scrollTo(0, currentScrollY);
      }
      if (onError) {
        onError(error);
      }
    };

    // Use rerenderSections with resolved section IDs
    await rerenderSections(
      sectionIds,
      sectionCallbacks,
      () => {
        // After rerendering, trigger reinitialization for all affected sections
        sectionIds.forEach((sectionId) => {
          const callback = sectionRerenderRegistry.get(sectionId);
          if (callback) {
            callback();
          }
        });

        // Reinitialize all sections of the affected types
        sectionTypes.forEach((sectionType) => {
          const sections = document.querySelectorAll(`[data-section-type="${sectionType}"]`);

          sections.forEach((section) => {
            const sectionId = section.getAttribute('data-section-id');

            if (sectionId) {
              // Try to find the section instance and reinitialize it
              if (window.BaseSection && typeof window.BaseSection.getInstance === 'function') {
                const instanceData = window.BaseSection.getInstance(sectionId);

                if (instanceData && instanceData.section) {
                  // Reset initialization flag and reinitialize
                  instanceData.initialized = false;
                  instanceData.section._initInstance(instanceData);
                }
              }
            }
          });
        });
        if (wrappedOnComplete) {
          wrappedOnComplete();
        }
      },
      wrappedOnError,
    );
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

/**
 * Unregisters a section rerender callback
 * @param {string} sectionId - The section ID
 */
export function unregisterSectionRerenderCallback(sectionId) {
  sectionRerenderRegistry.delete(sectionId);
}

/**
 * Updates a section's content with new HTML
 * @param {HTMLElement} container - The section container element
 * @param {string} sectionHtml - The new section HTML
 * @param {string} sectionId - The section ID
 * @returns {Promise<void>}
 */
async function updateSectionContent(container, sectionHtml, sectionId) {
  // Parse the new section HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sectionHtml;

  // Find the new section element
  const newSection = tempDiv.querySelector(`[data-section-id="${sectionId}"]`);
  if (!newSection) {
    throw new Error(`New section not found in response HTML for section: ${sectionId}`);
  }

  // Clean up existing Vue app if present
  const currentApp = container.__vue_app__;
  if (currentApp) {
    // Find the section instance and call onUnload if it exists
    const sectionId = container.getAttribute('data-section-id');

    // Try to find the section instance using BaseSection.getInstance
    if (window.BaseSection && typeof window.BaseSection.getInstance === 'function') {
      const instanceData = window.BaseSection.getInstance(sectionId);
      if (
        instanceData &&
        instanceData.section &&
        typeof instanceData.section.onUnload === 'function'
      ) {
        instanceData.section.onUnload(instanceData);
      }
    }

    currentApp.unmount();
    delete container.__vue_app__;
  }

  // Update container content
  container.innerHTML = newSection.innerHTML;

  // Update data attributes
  newSection
    .getAttributeNames()
    .filter((attr) => attr.startsWith('data-'))
    .forEach((attr) => {
      container.setAttribute(attr, newSection.getAttribute(attr));
    });
}
